/* eslint-disable @typescript-eslint/no-explicit-any */
import { isActivity, isBilling } from 'services/roles-services'
import { ReportKpiEntry } from 'utils/__interfaces/Documents/DocumentEntity'
import { EnumTypeDocumentWdh } from 'utils/__enums/MyDocuments/TypesDocument'
import { PERIMETER_LIMIT_FOR_DOCUMENTS } from 'utils/__consts/Common/Global'
import { OrganizationType } from 'store/state'
import getTotalInstitutions from 'utils/Perimeter/organization-utils'
import moment from 'moment'

export const isNotBilling = (typeDocument: string): boolean => {
  return [
    EnumTypeDocumentWdh.BI.toString(),
    EnumTypeDocumentWdh.BP.toString(),
    EnumTypeDocumentWdh.ATTESTATION_VALO.toString(),
  ].includes(typeDocument)
}

export const checkCategoriesByRoles = (roleNames: any): string => {
  // RDG: Un rôle activités ne doit voir que les catégories Administratif et Vie du contrat
  // RDG: Un rôle facturation ne doit voir que les catégories Administratif et Contrat
  const roleActivity = isActivity(roleNames)
  const roleBilling = isBilling(roleNames)

  if (!roleActivity && roleBilling) return 'Vie du contrat'
  if (!roleBilling && roleActivity) return 'Contrat'
  return ''
}

// Prepare data for download KPI
export const kpiDownloadDocumentData = (
  reportData: Record<string, ReportKpiEntry>,
  docType: string,
  status: number
) => {
  const updatedReportData = { ...reportData }

  if (!updatedReportData[docType]) {
    updatedReportData[docType] = { success: 0, unfound: 0, failure: 0 }
  }

  if (status === 200) {
    updatedReportData[docType].success += 1
  } else if ([404, 204].includes(status)) {
    updatedReportData[docType].unfound += 1
  } else {
    updatedReportData[docType].failure += 1
  }

  return updatedReportData
}

const getDocumentDownloadFilename = (
  typeDocument: string,
  numDocument: string
): string => {
  switch (typeDocument) {
    case EnumTypeDocumentWdh.FACTURE:
    case EnumTypeDocumentWdh.BRM:
    case EnumTypeDocumentWdh.AVOIR:
      return `${typeDocument}_N°${numDocument}.pdf`
    case EnumTypeDocumentWdh.BI:
      return `Bon_d'intervention_N°${numDocument}.pdf`
    case EnumTypeDocumentWdh.BP:
      return `Bon_de_pesée_N°${numDocument}.pdf`
    case EnumTypeDocumentWdh.FICHE_DECLASSEMENT:
      return `Fiche_de_déclassement_N°${numDocument}.pdf`
    default:
      return ''
  }
}

export const getDocumentInfosById = (
  id: string
): {
  idDocument: string
  typeDocument: string
  numDocument: string
  fileName: string
} => {
  const [idDocument, typeDocument, numDocument] = id.split(';')
  return {
    idDocument,
    typeDocument,
    numDocument,
    fileName: getDocumentDownloadFilename(typeDocument, numDocument),
  }
}

export const handleMimeType = (mimeType: string): string => {
  switch (mimeType) {
    case 'application/pdf':
      return 'pdf'
    case 'application/x-pdf':
      return 'pdf'
    case 'image/jpeg':
      return 'jpeg'
    case 'image/jpg':
      return 'jpg'
    case 'application/msword':
      return 'doc'
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'docx'
    case 'application/vnd.ms-powerpoint':
      return 'ppt'
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'pptx'
    case 'application/vnd.ms-excel':
      return 'xls'
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'xlsx'
    default:
      return ''
  }
}

// Vérifier si le client interne a plus de 20 établissements dans son périmètre
export const checkPerimeterLimit = (
  isInternalUser: boolean,
  institution: number,
  institutionPerimeter: number,
  organizations: OrganizationType
) => {
  // Condition à vérifier uniquement pour les internes
  if (!isInternalUser) return false
  if (institution > 0) return institution > PERIMETER_LIMIT_FOR_DOCUMENTS // On vérifie d'abord si l'utilisateur a filtré par établissements
  // Ensuite, On vérifie si l'utilisateur a filtré par organisations
  if (organizations.items?.length > 0)
    return getTotalInstitutions(organizations) > PERIMETER_LIMIT_FOR_DOCUMENTS
  // Sinon, on vérifie le périmètre (le undefined est nécessaire lorsque l'utilisateur rafraichie la page)
  return (
    institutionPerimeter === undefined ||
    institutionPerimeter > PERIMETER_LIMIT_FOR_DOCUMENTS
  )
}

export const getTooltipLabelForDownloadButton = (
  isGedMaintenance: boolean,
  isRowDisabled: boolean,
  hasAccessDemands: boolean,
  isWithinWaitingPeriod: boolean
) => {
  if (isRowDisabled && hasAccessDemands && !isWithinWaitingPeriod)
    return 'demandForUnfoundDocument'
  if (isGedMaintenance) return 'downloadNotAvailable'
  return 'downloadNotAvailableCustom'
}

// RG : Les documents de facturation du mois échu sont mis en ligne autour du 12 du mois courant.
export const isCurrentMonthBefore12th = (dateString: string): boolean => {
  const docDate = moment(dateString, 'MM/YYYY')
  if (moment().date() < 12) {
    return (
      docDate.year() === moment().year() &&
      docDate.month() >= moment().month() - 1
    )
  }
  return false
}

// RG : vérifier si un document est dans une période d'attente de 5 jours apres la date d'emission
export const isDocumentWithinWaiting5Days = (dateString: string): boolean => {
  const docDate = moment(dateString, 'DD/MM/YYYY')
  if (!docDate.isValid()) {
    return false
  }
  const availableDate = docDate.clone().add(5, 'days')
  return moment().isBefore(availableDate, 'day')
}
